import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./InvitationPage.css";

const flowerImages = [
  "/assets/flowers/flower-pink-2_jfhp2b.png",
  "/assets/flowers/flower-blue_m3h8ji.png",
  "/assets/flowers/flower-blue-2_vtwema.png",
  "/assets/flowers/flower-white_lt09t1.png",
];

interface FlowerData {
  id: number;
  left: number;
  delay: number;
  duration: number;
  size: number;
  image: string;
  swayAmount: number;
  opacity: number;
}

interface HeartData {
  id: number;
  left: number;
  delay: number;
  duration: number;
  size: number;
  emoji: string;
}

interface SparkleData {
  id: number;
  left: number;
  top: number;
  delay: number;
  duration: number;
  size: number;
}

const InvitationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const guestName = searchParams.get("to") || "Tamu Undangan";
  const navigate = useNavigate();

  const flowers = useMemo<FlowerData[]>(() => {
    return Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 8 + Math.random() * 14,
      size: 20 + Math.random() * 35,
      image: flowerImages[Math.floor(Math.random() * flowerImages.length)],
      swayAmount: 30 + Math.random() * 60,
      opacity: 0.3 + Math.random() * 0.4,
    }));
  }, []);

  const hearts = useMemo<HeartData[]>(() => {
    const heartEmojis = ["💕", "❤️", "💗", "💖", "🤍", "💞"];
    return Array.from({ length: 15 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 10,
      duration: 8 + Math.random() * 10,
      size: 12 + Math.random() * 16,
      emoji: heartEmojis[Math.floor(Math.random() * heartEmojis.length)],
    }));
  }, []);

  const sparkles = useMemo<SparkleData[]>(() => {
    return Array.from({ length: 25 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      delay: Math.random() * 5,
      duration: 2 + Math.random() * 3,
      size: 2 + Math.random() * 4,
    }));
  }, []);

  const handleContinue = () => {
    navigate("/invitation");
  };

  return (
    <div className="invitation-page">
      {/* Floating Hearts Background */}
      <div className="hearts-container">
        {hearts.map((heart) => (
          <div
            key={`heart-${heart.id}`}
            className="floating-heart"
            style={{
              left: `${heart.left}%`,
              fontSize: `${heart.size}px`,
              animationDelay: `${heart.delay}s`,
              animationDuration: `${heart.duration}s`,
            }}
          >
            {heart.emoji}
          </div>
        ))}
      </div>

      {/* Sparkle overlay */}
      <div className="sparkle-container">
        {sparkles.map((s) => (
          <div
            key={`sparkle-${s.id}`}
            className="sparkle"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Falling Flowers Background */}
      <div className="flowers-container">
        {flowers.map((flower) => (
          <motion.div
            key={flower.id}
            className="flower"
            style={{
              left: `${flower.left}%`,
              width: `${flower.size}px`,
              height: `${flower.size}px`,
              opacity: flower.opacity,
            }}
            initial={{ top: "-10%", rotate: 0, x: 0 }}
            animate={{
              top: "110%",
              rotate: [0, 180, 360],
              x: [0, flower.swayAmount, -flower.swayAmount, 0],
            }}
            transition={{
              duration: flower.duration,
              delay: flower.delay,
              repeat: Infinity,
              ease: "linear",
              x: {
                duration: flower.duration / 2,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          >
            <img src={flower.image} alt="" />
          </motion.div>
        ))}
      </div>

      {/* Decorative corner flowers */}
      <img
        src="/assets/flowers/flower-blue_m3h8ji.png"
        alt=""
        className="corner-flower top-left"
      />
      <img
        src="/assets/flowers/flower-pink-2_jfhp2b.png"
        alt=""
        className="corner-flower top-right"
      />
      <img
        src="/assets/flowers/flower-white_lt09t1.png"
        alt=""
        className="corner-flower bottom-left"
      />
      <img
        src="/assets/flowers/flower-blue-2_vtwema.png"
        alt=""
        className="corner-flower bottom-right"
      />

      {/* Content */}
      <div className="content">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="invitation-card"
        >
          {/* Ornamental Line */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="ornamental-line"
          >
            <span className="ornament-side" />
            <span className="ornament-center">✦</span>
            <span className="ornament-side" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="bismillah"
          >
            بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيم
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="subtitle"
          >
            The Wedding of
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.9, duration: 1.2, ease: "easeOut" }}
            className="names"
          >
            <h2>Imran</h2>
            <span className="ampersand">&amp;</span>
            <h2>Sofia</h2>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="wedding-date-home"
          >
            Senin, 06 April 2026
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 1 }}
            className="guest-section"
          >
            <p className="dear-text">Dear,</p>
            <p className="guest-name">{guestName}</p>
            <p className="invite-text">You are cordially invited</p>
          </motion.div>

          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 2.2, duration: 0.6, ease: "easeOut" }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="continue-btn"
            onClick={handleContinue}
          >
            💌 Buka Undangan
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default InvitationPage;
